import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, Bell, BellRing, Receipt, XCircle, Volume2 } from "lucide-react";
import { pub, money, API } from "@/lib/api";
import { HeaderLogo } from "@/components/Brand";
import { PoweredBy, Spinner, PracticeBanner } from "@/components/Layout";
import { TotalsBlock } from "@/components/CartSheet";
import { play, unlockAudio, soundReady, soundForTransition } from "@/lib/sound";
import { useLive } from "@/lib/realtime";

const STEPS = [["paid", "Paid"], ["preparing", "Preparing"], ["ready", "Ready"], ["picked_up", "Picked up"]];
const MANUAL_STEPS = [["awaiting_approval", "Accepted"], ["preparing", "Preparing"], ["ready", "Ready"], ["picked_up", "Picked up"]];
const ENDED = ["cancelled", "rejected", "expired", "no_show"];

const Stepper = ({ status, manual }) => {
  const steps = manual ? MANUAL_STEPS : STEPS;
  const idx = status === "awaiting_approval" ? -1 : steps.findIndex(([s]) => s === status);
  return (
    <ol className="mt-6 grid grid-cols-4 gap-2" data-testid="order-stepper">
      {steps.map(([s, label], i) => {
        const done = i <= idx;
        const isReady = s === "ready" && s === status;
        return (
          <li key={s} className="text-center" data-testid={`step-${s}`} data-active={done}>
            <div className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full border-2 ${isReady ? "border-ink bg-white text-ink" : done ? "border-brand bg-brand text-white" : "border-line bg-white text-muted2"}`}>
              {done ? <Check size={16} /> : i + 1}
            </div>
            <div className="mt-1 text-xs font-bold">{label}</div>
          </li>
        );
      })}
    </ol>
  );
};

export const readyLine = (o) => `Your order ${o.number} is ready!${o.ready_by ? ` Prepared by ${o.ready_by}.` : ""}`;

const recent = (iso, secs) => iso && Date.now() - new Date(iso).getTime() < secs * 1000;

function useStageAlerts(order, fromCheckout) {
  const prev = useRef(null);
  useEffect(() => {
    if (!order) return;
    const p = prev.current;
    prev.current = order.status;
    if (p === order.status) return;
    if (p === null) {
      const placed = (order.status === "paid" && fromCheckout && recent(order.paid_at, 120)) || (order.status === "awaiting_approval" && recent(order.created_at, 60));
      if (placed) play("payment_confirmed");
      return;
    }
    const s = soundForTransition(p, order.status, "customer");
    if (s) play(s);
    if (order.status === "ready") {
      if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
      if ("Notification" in window && Notification.permission === "granted") {
        try { new Notification(`LineGone · ${readyLine(order)}`, { body: `Pick it up at ${order.venue.name}.`, icon: "/brand/icon-192.png" }); } catch { /* ignore */ }
      }
    }
  }, [order, fromCheckout]);
}

const SoundPrompt = () => {
  const [on, setOn] = useState(soundReady());
  useEffect(() => { const t = setInterval(() => setOn(soundReady()), 1000); return () => clearInterval(t); }, []);
  const enable = async () => {
    unlockAudio();
    play("payment_confirmed");
    if ("Notification" in window && Notification.permission === "default") { try { await Notification.requestPermission(); } catch { /* ignore */ } }
    setOn(true);
  };
  return (
    <div className="mt-5 rounded-2xl border border-line bg-white p-4 text-ink" data-testid="keep-open-message">
      <p className="font-semibold">Keep this page open — it updates live and plays a sound at every step.</p>
      <button onClick={enable} className={on ? "btn-secondary mt-3 w-full" : "btn-primary mt-3 w-full"} data-testid="enable-alerts-btn">
        {on ? <><BellRing size={18} /> Sound & alerts on</> : <><Bell size={18} /> Tap to turn on sound</>}
      </button>
    </div>
  );
};

const SoundBar = () => {
  const [hidden, setHidden] = useState(soundReady());
  if (hidden) return null;
  return (
    <button onClick={() => { unlockAudio(); play("payment_confirmed"); if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {}); setHidden(true); }}
      className="fixed inset-x-3 bottom-3 z-40 flex items-center justify-center gap-2 rounded-full bg-ink py-4 font-heading text-lg font-black text-white shadow-2xl" data-testid="customer-sound-prompt">
      <Volume2 size={20} /> Tap to turn on sound
    </button>
  );
};

export default function OrderStatus() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState(false);
  const [params] = useSearchParams();
  const [tick, setTick] = useState(0);
  useStageAlerts(o, !!params.get("session_id"));
  const connected = useLive(`order/${orderId}`, () => setTick((t) => t + 1));

  useEffect(() => {
    let alive = true;
    const load = () => pub.get(`/orders/${orderId}`).then((r) => alive && setO(r.data)).catch(() => alive && setMissing(true));
    load();
    const t = setInterval(load, connected ? 15000 : 3000);
    return () => { alive = false; clearInterval(t); };
  }, [orderId, connected, tick]);
  useEffect(() => { if (o) document.title = `Order ${o.number} · LineGone`; }, [o]);

  if (missing) return <div className="p-10 text-center" data-testid="order-not-found">Order not found.</div>;
  if (!o) return <Spinner />;
  const ready = o.status === "ready";
  const cancelled = ENDED.includes(o.status);
  const bg = ready ? "bg-ready" : cancelled ? "bg-danger-pale" : "bg-brand-pale";
  const headline = { pending_payment: "Confirming your payment…", awaiting_approval: "Waiting for the venue to accept your order.", paid: "Paid — sent to the counter", preparing: o.manual ? "Accepted — preparing your order" : "Preparing your order", ready: "Ready! Come grab it.", picked_up: "Picked up. Enjoy!", cancelled: "Order cancelled & refunded", rejected: "Sorry, the venue couldn't accept your order", expired: "Your order expired", no_show: "Order marked as not collected" }[o.status];
  const endNote = { cancelled: `${o.cancel_reason} — refunded in full including GST.`, rejected: o.reject_reason ? `Reason: ${o.reject_reason}` : "No reason was given.", expired: "The venue didn't accept it in time. You haven't been charged — please order again or ask at the counter.", no_show: "The venue marked this order as never collected." }[o.status];
  return (
    <div className={`min-h-screen transition-colors duration-700 ${bg}`} data-testid="order-status-page" data-status={o.status}>
      <header className="flex h-14 items-center justify-between bg-white/80 px-4 backdrop-blur"><HeaderLogo className="h-6" /><span className="text-sm font-semibold">{o.venue.name}</span></header>
      {o.practice && <PracticeBanner text="PRACTICE ORDER — Stripe test card, no real money moved" testid="status-practice-banner" />}
      <main className="mx-auto max-w-md px-4 pb-28 pt-8 text-center text-ink">
        <div className="eyebrow !text-ink">Order number</div>
        <div className={`mx-auto mt-2 inline-block rounded-3xl bg-white px-8 py-3 font-heading text-7xl font-black sm:text-8xl ${ready ? "pulse-amber" : ""}`} data-testid="order-number">{o.number}</div>
        <h1 className="mt-5 text-3xl font-black" data-testid="order-status-headline">{headline}</h1>
        {ready && <p className="mt-2 text-lg font-bold" data-testid="ready-by-line">{readyLine(o)}</p>}
        <p className="mt-1 text-sm">for {o.customer_name}</p>
        {cancelled && <p className="mt-3 flex items-center justify-center gap-2 font-semibold text-danger" data-testid="order-cancel-reason"><XCircle size={18} /> {endNote}</p>}
        {!cancelled && <Stepper status={o.status} manual={o.manual} />}
        {o.manual && !cancelled && <div className="mt-5 rounded-2xl bg-white p-4 font-heading text-xl font-black" data-testid="status-pay-at-pickup">
          {o.status === "picked_up" ? `Paid at pickup · ${money(o.totals.total_cents)}` : `Pay at pickup: ${money(o.totals.total_cents)}`}<div className="font-body text-xs font-semibold text-muted2">incl. GST · {o.status === "picked_up" ? `paid by ${{ cash: "cash", card_terminal: "card at the venue", etransfer: "e-Transfer" }[o.paid_method] || "the venue"}` : "cash, card or e-Transfer at the counter"}</div></div>}
        {!cancelled && o.status !== "picked_up" && <><SoundPrompt /><SoundBar /></>}
        <div className="mt-5 rounded-2xl bg-white p-4 text-left">
          <ul className="mb-3 space-y-1 text-sm">{o.items.map((i, k) => <li key={k} className="flex justify-between"><span>{i.qty} × {i.name}{i.options.length ? ` (${i.options.join(", ")})` : ""}</span><span>{money(i.unit_cents * i.qty)}</span></li>)}</ul>
          <TotalsBlock t={o.totals} testPrefix="status" />
          {!["pending_payment", "awaiting_approval", "rejected", "expired"].includes(o.status) && !o.manual && <a href={`${API}/public/orders/${o.id}/receipt`} target="_blank" rel="noreferrer" className="link-lg mt-3 inline-flex items-center gap-1 text-sm" data-testid="view-receipt-link"><Receipt size={14} /> View receipt{o.has_email ? " (also emailed)" : ""}</a>}
        </div>
        <Link to={`/v/${o.venue.slug}`} className="link-lg mt-6 inline-block text-sm" data-testid="back-to-menu-link">Back to {o.venue.name} menu</Link>
        <PoweredBy />
      </main>
    </div>
  );
}
